import { importDataFromExcel } from '@/helpers/excel'
import { ApplicationsState, OfflineState, ConvocatoriasState } from '@/state'
import { applicationsStateActions } from '@/state/actions'
import applicationRequests from '@/state/applications/requests'
import { Button, notification, Upload, Modal } from 'antd'
import { RcFile } from 'antd/es/upload'
import { IoIosAttach } from 'react-icons/io'
import { consultarRPA, filtrarCedulasElegiblesConvocatoria, filtrarCedulasNoElegiblesConvocatoria } from '@/services/rpaService'
import React, { useState } from 'react'
import { EApplicationType } from '@/interfaces'
import * as XLSX from 'xlsx'

const allowedTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]

const FILE_ATTACH_COLUMNS = [
  'TIPO',
  'TIPO DOCUMENTO',
  'DOCUMENTO',
  'NOMBRES',
  'APELLIDOS',
]

export default function ImportFileAttach() {
  const [modalVisible, setModalVisible] = useState(false)
  const [modalContent, setModalContent] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState(false)

  // *** FUNCIÓN CORREGIDA DE POLLING ***
  async function pollJobStatus(jobId: string, cedulas: string[], excelRows: any[]) {
    console.log('[RPA DEBUG][BulkImport] Iniciando polling para job:', jobId)

    let completed = false
    let attempts = 0
    const maxAttempts = 40 // ~2 minutos
    const pollInterval = 3000 // 3 segundos

    // Mostrar notificación de progreso
    const progressNotificationKey = 'rpa-processing'
    notification.info({
      message: 'Procesando Consulta RPA',
      description: `Validando ${cedulas.length} cédulas en sistemas externos. Esto puede tomar unos minutos...`,
      placement: 'top',
      duration: 0,
      key: progressNotificationKey
    })

    while (!completed && attempts < maxAttempts) {
      try {
        console.log('[RPA DEBUG][BulkImport] Polling attempt:', attempts + 1)

        const statusResp = await fetch(`/rpa/status/${jobId}`)

        if (!statusResp.ok) {
          throw new Error(`HTTP ${statusResp.status}: ${statusResp.statusText}`)
        }

        const statusData = await statusResp.json()
        console.log('[RPA DEBUG][BulkImport] Status response:', statusData)

        if (statusData.status === 'COMPLETED') {
          completed = true
          console.log('[RPA DEBUG][BulkImport] Job completed!')
          break
        } else if (statusData.status === 'FAILED' || statusData.status === 'ERROR') {
          throw new Error(`Job failed: ${statusData.error || 'Unknown error'}`)
        }

        // Mostrar progreso si está disponible
        if (statusData.progress_percentage) {
          notification.info({
            message: 'Procesando Consulta RPA',
            description: `Progreso: ${statusData.progress_percentage}% - ${statusData.completed || 0}/${statusData.total_cedulas || cedulas.length} cédulas procesadas`,
            placement: 'top',
            duration: 0,
            key: progressNotificationKey
          })
        }

      } catch (e) {
        console.error('[RPA DEBUG][BulkImport] Error en polling:', e)

        // Si falla el polling, intentar de nuevo hasta el límite
        if (attempts >= maxAttempts - 1) {
          notification.destroy(progressNotificationKey)
          setModalContent(`Error en el monitoreo del proceso RPA.\n\nJob ID: ${jobId}\nIntentos: ${attempts + 1}\nError: ${getErrorMessage(e)}\n\nPor favor, verifica el estado manualmente o contacta al administrador.`)
          setModalVisible(true)
          setIsProcessing(false)
          return
        }
      }

      await new Promise(res => setTimeout(res, pollInterval))
      attempts++
    }

    // Cerrar notificación de progreso
    notification.destroy(progressNotificationKey)

    if (!completed) {
      setModalContent(`El proceso RPA no se completó en el tiempo esperado.\n\nJob ID: ${jobId}\nTiempo transcurrido: ${(attempts * pollInterval / 1000).toFixed(0)} segundos\n\nPuede que el proceso aún esté ejecutándose. Verifica el estado más tarde.`)
      setModalVisible(true)
      setIsProcessing(false)
      return
    }

    // *** OBTENER RESULTADOS CONSOLIDADOS ***
    try {
      console.log('[RPA DEBUG][BulkImport] Obteniendo resultados consolidados para job:', jobId)

      const consolidadoResp = await fetch(`/rpa/job/${jobId}/consolidated`)

      if (!consolidadoResp.ok) {
        throw new Error(`HTTP ${consolidadoResp.status}: ${consolidadoResp.statusText}`)
      }

      const consolidado = await consolidadoResp.json()
      console.log('[RPA DEBUG][BulkImport] Consolidado obtenido:', consolidado)

      if (!consolidado || consolidado.status !== 'COMPLETED') {
        throw new Error('Consolidado no está disponible o no se completó')
      }

      // *** USAR FUNCIONES CORREGIDAS PARA FILTRAR CÉDULAS ***
      const cedulasElegibles = filtrarCedulasElegiblesConvocatoria(consolidado)
      const cedulasInhabilitadas = filtrarCedulasNoElegiblesConvocatoria(consolidado)

      // --- BLOQUEO INMEDIATO SI HAY CÉDULAS INHABILITADAS ---
      if (cedulasInhabilitadas.length > 0) {
        setModalContent(
          `No se creó ninguna postulación porque el jefe de hogar o uno de los integrantes del grupo familiar tiene propiedad registrada en sistemas externos.\n\nCédulas inhabilitadas: ${cedulasInhabilitadas.join(", ")}`
        )
        setModalVisible(true)
        setIsProcessing(false)
        // Aquí puedo agregar lógica para cambiar el estado visual a "Inhabilitado" si aplica
        // Por ejemplo: setEstadoSolicitud('INHABILITADO')
        return
      }

      if (cedulasElegibles.length === 0 && cedulasInhabilitadas.length === 0) {
        setModalContent('No se obtuvieron resultados del proceso RPA. Verifica la configuración del servicio.')
        setModalVisible(true)
        setIsProcessing(false)
        return
      }

      // *** PROCESAR POSTULACIONES MASIVAS SOLO SI NO HAY INHABILITADAS ***
      const resultados = await procesarPostulacionesMasivas(
        cedulasElegibles,
        cedulasInhabilitadas,
        excelRows,
        consolidado
      )

      // *** MOSTRAR RESULTADO FINAL ***
      mostrarResultadoFinal(resultados, cedulasElegibles, cedulasInhabilitadas)

    } catch (error) {
      console.error('[RPA DEBUG][BulkImport] Error obteniendo consolidado:', error)
      setModalContent(`Error obteniendo los resultados del proceso RPA.\n\nJob ID: ${jobId}\nError: ${getErrorMessage(error)}\n\nVerifica que el servicio RPA esté funcionando correctamente.`)
      setModalVisible(true)
    }

    setIsProcessing(false)
  }

  // *** FUNCIÓN CORREGIDA PARA PROCESAR POSTULACIONES MASIVAS ***
  async function procesarPostulacionesMasivas(
    cedulasElegibles: string[],
    cedulasInhabilitadas: string[],
    excelRows: any[],
    consolidado: any
  ) {
    const resultados = {
      exitosos: [] as string[],
      fallidos: [] as {cedula: string, error: string}[],
      inhabilitados: cedulasInhabilitadas
    }

    console.log('[RPA DEBUG][BulkImport] Iniciando creación de postulaciones para', cedulasElegibles.length, 'cédulas elegibles')

    // *** PROCESAR SOLO LAS CÉDULAS ELEGIBLES (SIN PROPIEDADES) USANDO IMPORTACIÓN MASIVA ***
    if (cedulasElegibles.length > 0) {
      try {
        // Filtrar solo las filas del Excel que corresponden a cédulas elegibles
        const filasElegibles = excelRows.filter((row: any) => {
          const docExcel = String(row['DOCUMENTO']).replace(/[.,\s]/g, '').trim()
          return cedulasElegibles.some(cedula => {
            const docComparar = cedula.replace(/[.,\s]/g, '').trim()
            return docExcel === docComparar
          })
        })

        // Crear el archivo base64 solo con las cédulas elegibles
        const datosParaImportar = filasElegibles.map((row: any) => ({
          tipo: row['TIPO'],
          tipoDocumento: row['TIPO DOCUMENTO'],
          documento: row['DOCUMENTO'],
          nombres: row['NOMBRES'],
          apellidos: row['APELLIDOS'],
          // Agregar otros campos necesarios según la estructura del Excel
        }))

        // Convertir los datos a formato Excel y luego a base64
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(datosParaImportar)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Postulaciones')

        // Generar archivo Excel temporal
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const archivoBase64 = btoa(String.fromCharCode(...new Uint8Array(excelBuffer)))

        // *** USAR EL MÉTODO CORRECTO DE IMPORTACIÓN MASIVA ***
        await applicationsStateActions.importData(archivoBase64)

        // Marcar todas las cédulas elegibles como exitosas
        resultados.exitosos.push(...cedulasElegibles)
        console.log('[RPA DEBUG][BulkImport] Importación masiva exitosa para', cedulasElegibles.length, 'cédulas')

      } catch (error) {
        console.error('[RPA DEBUG][BulkImport] Error en importación masiva:', error)
        // Si falla la importación masiva, marcar todas las cédulas como fallidas
        cedulasElegibles.forEach(cedula => {
          resultados.fallidos.push({
            cedula,
            error: `Error en importación masiva: ${getErrorMessage(error)}`
          })
        })
      }
    }

    return resultados
  }


  // *** FUNCIÓN PARA MOSTRAR RESULTADO FINAL ***
  function mostrarResultadoFinal(
    resultados: any,
    cedulasElegibles: string[],
    cedulasInhabilitadas: string[]
  ) {
    const totalExitosos = resultados.exitosos.length
    const totalFallidos = resultados.fallidos.length
    const totalInhabilitados = cedulasInhabilitadas.length

    let mensaje = `IMPORTACIÓN MASIVA FINALIZADA\n\n`

    mensaje += `📊 RESUMEN GENERAL:\n`
    mensaje += `   • Total cédulas procesadas: ${cedulasElegibles.length + totalInhabilitados}\n`
    mensaje += `   • Cédulas elegibles: ${cedulasElegibles.length}\n`
    mensaje += `   • Cédulas inhabilitadas: ${totalInhabilitados}\n\n`

    if (totalExitosos > 0) {
      mensaje += `✅ POSTULACIONES CREADAS: ${totalExitosos}\n`
      mensaje += `   Cédulas: ${resultados.exitosos.slice(0, 10).join(', ')}`
      if (resultados.exitosos.length > 10) {
        mensaje += ` y ${resultados.exitosos.length - 10} más...\n\n`
      } else {
        mensaje += `\n\n`
      }
    }

    if (totalInhabilitados > 0) {
      mensaje += `❌ CÉDULAS INHABILITADAS POR RPA: ${totalInhabilitados}\n`
      mensaje += `   (Tienen propiedades registradas)\n`
      mensaje += `   Cédulas: ${cedulasInhabilitadas.slice(0, 10).join(', ')}`
      if (cedulasInhabilitadas.length > 10) {
        mensaje += ` y ${cedulasInhabilitadas.length - 10} más...\n\n`
      } else {
        mensaje += `\n\n`
      }
    }

    if (totalFallidos > 0) {
      mensaje += `⚠️ ERRORES EN CREACIÓN: ${totalFallidos}\n`
      resultados.fallidos.slice(0, 5).forEach((fallo: any) => {
        mensaje += `   • ${fallo.cedula}: ${fallo.error}\n`
      })
      if (resultados.fallidos.length > 5) {
        mensaje += `   ... y ${resultados.fallidos.length - 5} errores más\n`
      }
      mensaje += `\n`
    }

    if (totalExitosos === 0 && totalFallidos === 0) {
      mensaje += `ℹ️ No se crearon postulaciones. Todas las cédulas están inhabilitadas por tener propiedades registradas.`
    }

    setModalContent(mensaje)
    setModalVisible(true)

    // *** MOSTRAR NOTIFICACIÓN RESUMEN ***
    if (totalExitosos > 0) {
      notification.success({
        message: 'Importación Masiva Completada',
        description: `✅ ${totalExitosos} postulaciones creadas\n❌ ${totalInhabilitados} cédulas inhabilitadas`,
        placement: 'top',
        duration: 8,
      })
    } else {
      notification.warning({
        message: 'Importación Masiva Completada',
        description: `❌ ${totalInhabilitados} cédulas inhabilitadas\n⚠️ ${totalFallidos} errores\n❌ 0 postulaciones creadas`,
        placement: 'top',
        duration: 8,
      })
    }

    // *** REFRESCAR LISTA DE APLICACIONES ***
    applicationsStateActions.getAllApplications()
  }

  // *** FUNCIÓN beforeUpload CORREGIDA ***
  function beforeUpload(file: RcFile) {
    if (isProcessing) {
      notification.warning({
        message: 'Procesamiento en curso',
        description: 'Ya hay un proceso de importación en curso. Por favor espera a que termine.',
        placement: 'top',
      })
      return false
    }

    const isGt10M = file.size / 1024 / 1024 > 10

    if (isGt10M) {
      notification.warning({
        message: 'Tamaño no permitido',
        description: 'El archivo supera el límite de 10 MB. Intenta con un archivo más pequeño.',
        placement: 'top',
      })
      return false
    }

    if (!allowedTypes.includes(file.type)) {
      notification.warning({
        message: 'Tipo de archivo no permitido',
        description: 'Solo se permiten archivos .XLS, .XLSX o .CSV',
        placement: 'top',
      })
      return false
    }

    const readerValidation = new FileReader()

    readerValidation.onload = async () => {
      try {
        const fileValidationData = readerValidation.result

        const { jsonData, validStructure } = importDataFromExcel({
          fileData: fileValidationData,
          structure: FILE_ATTACH_COLUMNS,
        })

        if (!validStructure) {
          notification.warning({
            message: 'Estructura no permitida',
            description: 'La estructura del archivo no corresponde a la esperada. Columnas requeridas: TIPO, TIPO DOCUMENTO, DOCUMENTO, NOMBRES, APELLIDOS',
            duration: 10,
          })
          return
        }

        if (!jsonData.length) {
          notification.warning({
            message: 'Archivo vacío',
            description: 'El archivo no contiene datos para procesar.',
            duration: 10,
          })
          return
        }

        // *** EXTRACCIÓN Y VALIDACIÓN DE CÉDULAS ***
        let cedulas = jsonData
          .map((row: any) => row['DOCUMENTO'])
          .filter(Boolean)
          .map(c => String(c).replace(/[.,\s]/g, '').trim())
          .filter(c => c.length > 0)

        cedulas = Array.from(new Set(cedulas)) // Eliminar duplicados

        console.log('[RPA DEBUG][BulkImport] Cédulas extraídas del Excel:', cedulas)

        if (cedulas.length === 0) {
          notification.warning({
            message: 'Sin cédulas válidas',
            description: 'No se encontraron cédulas válidas en el archivo.',
            placement: 'top',
          })
          return
        }

        setIsProcessing(true)

        // *** CONSULTA RPA MASIVA ***
        try {
          console.log('[RPA DEBUG][BulkImport] Iniciando consulta RPA masiva para', cedulas.length, 'cédulas')

          const rpaResult = await consultarRPA(cedulas)
          console.log('[RPA DEBUG][BulkImport] Resultado RPA:', rpaResult)

          if (rpaResult.error) {
            throw new Error(rpaResult.error)
          }

          if (rpaResult.isAsync && rpaResult.jobId) {
            // *** RESPUESTA ASÍNCRONA - INICIAR POLLING ***
            console.log('[RPA DEBUG][BulkImport] Iniciando polling para job:', rpaResult.jobId)
            await pollJobStatus(rpaResult.jobId, cedulas, jsonData)
          } else {
            // *** RESPUESTA SÍNCRONA - PROCESAR INMEDIATAMENTE ***
            console.log('[RPA DEBUG][BulkImport] Procesando resultado síncrono')

            const cedulasElegibles = filtrarCedulasElegiblesConvocatoria(rpaResult)
            const cedulasInhabilitadas = filtrarCedulasNoElegiblesConvocatoria(rpaResult)

            // --- BLOQUEO INMEDIATO SI HAY CÉDULAS INHABILITADAS ---
            if (cedulasInhabilitadas.length > 0) {
              setModalContent(
                `No se creó ninguna postulación porque el jefe de hogar o uno de los integrantes del grupo familiar tiene propiedad registrada en sistemas externos.\n\nCédulas inhabilitadas: ${cedulasInhabilitadas.join(", ")}`
              )
              setModalVisible(true)
              setIsProcessing(false)
              // Aquí puedes agregar lógica para cambiar el estado visual a "Inhabilitado" si aplica
              // Por ejemplo: setEstadoSolicitud('INHABILITADO')
              return
            }

            if (cedulasElegibles.length === 0 && cedulasInhabilitadas.length === 0) {
              setModalContent('No se obtuvieron resultados del proceso RPA. Verifica la configuración del servicio.')
              setModalVisible(true)
              setIsProcessing(false)
              return
            }

            // *** PROCESAR POSTULACIONES MASIVAS SOLO SI NO HAY INHABILITADAS ***
            const resultados = await procesarPostulacionesMasivas(
              cedulasElegibles,
              cedulasInhabilitadas,
              jsonData,
              rpaResult
            )

            mostrarResultadoFinal(resultados, cedulasElegibles, cedulasInhabilitadas)
            setIsProcessing(false)
          }

        } catch (error) {
          console.error('[RPA DEBUG][BulkImport] Error en consulta RPA:', error)
          setModalContent(`Error en la consulta RPA masiva.\n\nError: ${getErrorMessage(error)}\n\nVerifica que el servicio RPA esté funcionando correctamente y vuelve a intentar.`)
          setModalVisible(true)
          setIsProcessing(false)
        }


      } catch (error) {
        console.error('[RPA DEBUG][BulkImport] Error procesando archivo:', error)
        notification.error({
          message: 'Error procesando archivo',
          description: 'Ocurrió un error al procesar el archivo. Verifica el formato y vuelve a intentar.',
          placement: 'top',
        })
        setIsProcessing(false)
      }
    }

    readerValidation.readAsArrayBuffer(file)
    return false
  }

  // Helper para extraer mensaje de error
  function getErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error
    if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
      return (error as any).message
    }
    return 'Error desconocido'
  }

  if (ApplicationsState.importApplicationType.value) {
    return (
      <>
        <Upload
          multiple={false}
          maxCount={1}
          accept={allowedTypes.join(', ')}
          beforeUpload={beforeUpload}
          showUploadList={false}
          disabled={isProcessing}
        >
          <Button
            title='Adjuntar archivo'
            className='import-attach-btn'
            shape='round'
            icon={<IoIosAttach size={16} />}
            loading={isProcessing || ApplicationsState.loading.value.import}
            disabled={
              isProcessing ||
              ApplicationsState.loading.value.getAll ||
              ApplicationsState.loading.value.import ||
              OfflineState.isSynchronizing.value
            }
          >
            {isProcessing ? 'Procesando RPA...' : 'Adjuntar archivo'}
          </Button>
        </Upload>
        <Modal
          title='Resultado de Importación Masiva con Validación Sistemas Externos'
          open={modalVisible}
          onOk={() => setModalVisible(false)}
          onCancel={() => setModalVisible(false)}
          centered
          width={700}
          styles={{
            body: {
              maxHeight: '500px',
              overflowY: 'auto'
            }
            // header: custom close icon color should be handled via CSS if needed
          }}
          okButtonProps={{
            style: { color: 'white', fontWeight: 'bold' }
          }}
          cancelButtonProps={{
            style: { color: 'white', fontWeight: 'bold' }
          }}
          closeIcon={<span style={{ color: 'white', fontSize: '16px' }}>×</span>}
        >
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '14px', color: 'black', fontWeight: 'bold' }}>{modalContent}</pre>
        </Modal>
      </>
    )
  }

  return null
}